import MagicCreditsIcon from "components/icons/MagicCredits"
import { showModal2 } from "components/utils/CustomModal"
import { BASE_API_URL } from "constants/General"
import { useAppState } from "graphql/hooks/useAppState"
import Lottie from "lottie-react"
import React, { useEffect, useRef, useState } from "react"
import intl from "react-intl-universal"
import { MagicCreditsInfo, MagicCreditsProps, MagicCreditsResponse } from "../types"
import AdditionalInfo from "./AdditionalInfo"
import magicCreditsAnimation from 'common/json/lottie/MagicCreditsAnimation.json'
import { extractGeminiErrorMessage } from 'utils/utils'
import { getAiCreditsState } from "../aiCredits"
import AiCreditsNotice from "./AiCreditsNotice"

const MagicCredits = ({
    magicData,
    creditsTokens,
    setCredits,
    showCreditsIndicator,
    setShowCreditsIndicator,
    courseId,
    organizationId,
    type,
    programLength,
    programLengthOther,
    outlineData,
    outlineDataStringArray,
    outlineCreditsArray,
    outlinePosition,
    skipEstimateTokens,
    canAssignAiTasks,
    alreadyRequested,
    onAiCreditsRequested } : MagicCreditsProps) => {
  const appState = useAppState()
  const DEBOUNCE_DELAY = 500
  // The whole credits block from the last estimate, not just the two numbers —
  // the creator's allocation has to survive to the render below.
  const [estimate, setEstimate] = useState<MagicCreditsInfo | null>(null)
  const [loading, setLoading] = useState(showCreditsIndicator)
  const controllerRef = useRef<AbortController | null>(null)

  /**
   * Abort whatever is in flight when this unmounts.
   *
   * Only a *new* estimate aborted the previous one, so closing Magic Creator
   * mid-estimate left the request running and then setting state on an unmounted
   * component.
   */
  useEffect(() => () => controllerRef.current?.abort(), [])

  useEffect(() => {
    if (!appState) return
    if (skipEstimateTokens) return

    const countProgramChapters = (!courseId && outlineData) ? outlineData.split('Session').length - 1 : undefined
    const validProgramLength = programLength && programLength === 'automatic' ? 6 : programLength === 'other' ? Number(programLengthOther) : Number(programLength)

    const hasValidOutline = outlineData?.trim() !== "";
    const hasOtherChanges =
      magicData.uploadedFilesData ||
      magicData.programLength ||
      magicData.generateAiImages

    if (countProgramChapters === validProgramLength) return

    // if outlineDataStringArray is the same as the credits array then we don't make the call,
    // the hack is that when we change the outlineDataStringArray we also change the outlineCreditsArray
    // but at first itteration they are not the same, so this appears to be working somehow

    if (outlineData && outlineDataStringArray?.length === outlineCreditsArray?.length) return

    if (hasValidOutline || hasOtherChanges) {
      const debounceTimer = setTimeout(() => {
        credits()
      }, DEBOUNCE_DELAY)

      return () => clearTimeout(debounceTimer)
    }

  }, [
    magicData.uploadedFilesData, magicData.programLength, magicData.generateAiImages, outlineData, appState,
    magicData.generateAiAudio, magicData.generateAiVideo, skipEstimateTokens, programLengthOther,
  ])

  if (!appState) return

  const credits = async () => {
    setLoading(true)
    setShowCreditsIndicator(true)
    // Abort the previous request if it exists
    if (controllerRef.current) {
      controllerRef.current.abort()
    }

    // Create a new AbortController
    const controller = new AbortController()
    controllerRef.current = controller

    // Determine the actual programLength value to send
    const programLengthValue = programLength === 'other' ? String(programLengthOther) : programLength

    try {
      const response = await fetch(`${BASE_API_URL}/api/v1/integrations/ai/magicCreator/estimateTokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${appState.loggedInAs.token}`,
        },
        body: JSON.stringify({
          uploadedFilesData: magicData.uploadedFilesData,
          prompt: '',
          generateAiImages: magicData.generateAiImages,
          generateAiAudio: magicData.generateAiAudio,
          generateAiVideo: magicData.generateAiVideo,
          courseId: courseId || '',
          organizationId: organizationId || '',
          type,
          programLength: programLengthValue,
          outline: outlineData,
        }),
        signal: controller.signal, // Pass the abort signal
      })

      if (!response.ok) {
        const error = await response.json()
        setShowCreditsIndicator(false)
        setLoading(false)
        throw new Error(error.error.message)
      }

      const result: MagicCreditsResponse = await response.json()

      setEstimate(result.credits)
      setCredits(result.credits)
      setShowCreditsIndicator(false)
      setLoading(false)
      controllerRef.current = null
    } catch (err) {
      if ((err as Error).name === "AbortError") {
        console.log("Fetch request was aborted.", err);
        return
      }
      showModal2({
        title: `Error`,
        content: extractGeminiErrorMessage((err as Error)?.message),
        destructive: true,
        primaryText: intl.get('global_close'),
        secondaryButton: false,
      })
      setShowCreditsIndicator(false)
      setLoading(false)
    }
  }
  const outlineEstimate = outlineCreditsArray[outlinePosition]

  const neededCredits =
    outlineEstimate?.totalCredits || estimate?.totalCredits || creditsTokens.totalCredits || 0

  // `??`, not `||`: zero available is the single most important number to get
  // right once credits are metered per creator, and `||` would treat it as
  // "unknown" and fall back to a stale figure from the parent.
  const availableCredits = estimate?.availableCredits ?? creditsTokens.availableCredits ?? 0

  // The creator's allocation is per workspace, not per outline, so every estimate
  // carries the same figures — take the freshest one that has them.
  const creator = outlineEstimate?.creator ?? estimate?.creator ?? creditsTokens.creator
  const creatorCreditsEnforced =
    outlineEstimate?.creatorCreditsEnforced ??
    estimate?.creatorCreditsEnforced ??
    creditsTokens.creatorCreditsEnforced

  const creditsInfo: MagicCreditsInfo = {
    totalTokens: estimate?.totalTokens ?? creditsTokens.totalTokens ?? 0,
    totalCredits: neededCredits,
    availableCredits,
    creatorCreditsEnforced,
    creator,
  }

  const creditsState = getAiCreditsState(creditsInfo)
  const statusClass = creditsState === 'notAssigned'
    || creditsState === 'insufficient'
    || creditsState === 'workspaceExhausted'
    ? 'text-coral'
    : creditsState === 'low'
      ? 'text-yellow-700'
      : ''

  return (
    <div data-test="ai-credits" className="flex flex-col items-start">
      <div className="flex flex-row items-center justify-start space-x-1 sm:space-x-4">
        {loading ? <Lottie className="w-10 h-10" animationData={magicCreditsAnimation}/> : <MagicCreditsIcon className="w-10 h-10 fill-magic" />}
        <div className={loading ? 'blur-sm animate-pulse': ''}>
          <div data-test="credit-count" className={`font-bold text-xs ${statusClass}`}>{neededCredits} {intl.get('settings_tasks')}</div>
          <div data-test="available-credits" className="flex items-center justify-start space-x-2 text-xs">
            <div className={statusClass}>
              {creditsState === 'notAssigned'
                ? intl.get('ai_credits_no_credits_assigned_short')
                : `${availableCredits} ${intl.get('global_available')}`}
            </div>
            <AdditionalInfo
              tooltipContent={<div>{creatorCreditsEnforced
                ? intl.get('magic_credits_tooltip_creator')
                : intl.getHTML('magic_credits_tooltip')}</div>}
              interactive={true} />
          </div>
        </div>
      </div>

      {/* Why this creator has no AI Tasks, and how to get some. Renders nothing
          unless the workspace meters credits per creator. */}
      {!loading && (
        <AiCreditsNotice
          credits={creditsInfo}
          organizationId={organizationId}
          canAssignAiTasks={canAssignAiTasks}
          alreadyRequested={alreadyRequested}
          onAiCreditsRequested={onAiCreditsRequested}
          compact={true} />
      )}
    </div>
  )
}

export default MagicCredits